import { Fragment, useState } from "react"
import { toast } from "react-toastify"
import { useUpdateExperimentMutation } from "~/ui-rtk/api/experimentApi"
import Button from "~/ui-rtk/components/ui/controls/Button"
import Input from "~/ui-rtk/components/ui/controls/Input"
import type { ExperimentRecord } from "~/ui-rtk/types/experiments"
import {
  describeDesignPower,
  formatMoney,
  formatPct,
} from "~/ui-rtk/utils/experiment-design-summary"

/**
 * What the ready design can detect, for this experiment's duration, plus
 * the same figure at every offered test length so the user can judge
 * whether a longer test (a new experiment) would help. Warn-only.
 */
export default function DesignPowerSummary({
  experiment,
}: {
  experiment: ExperimentRecord
}) {
  const [updateExperiment, { isLoading: isSaving }] =
    useUpdateExperimentMutation()
  const [budgetDraft, setBudgetDraft] = useState("")
  const [iroasDraft, setIroasDraft] = useState("")

  const summary = experiment.designSummary
  const verdict = summary ? describeDesignPower(experiment, summary) : null
  if (!summary || !verdict) {
    return null
  }

  const draftBudget = Number(budgetDraft)
  const draftIroas = Number(iroasDraft)
  const canSave = draftBudget > 0 || draftIroas > 0

  const handleSave = async () => {
    try {
      await updateExperiment({
        id: experiment.id,
        body: {
          ...(draftBudget > 0 ? { totalBudget: draftBudget } : {}),
          ...(draftIroas > 0 ? { expectedIroas: draftIroas } : {}),
        },
      }).unwrap()
    } catch {
      toast.error("Failed to save the budget guidance inputs")
    }
  }

  const budget = experiment.totalBudget
  const iroas = experiment.expectedIroas
  const anyExtrapolated = summary.horizons.some(
    horizon => horizon.method === "extrapolated",
  )

  return (
    <div className="flex flex-col gap-1 text-xs" data-testid="design-power">
      <span className="text-white">
        Detects ≥ {formatPct(verdict.mde)} lift over {experiment.durationDays}{" "}
        days
        <span className="text-basic-grey-inactive">
          {" "}
          (≈ {formatMoney(verdict.detectableLift, verdict.currency)} ·{" "}
          {verdict.powerPct}% power, {verdict.confidencePct}% CI
          {verdict.horizon.method === "extrapolated" ? ", extrapolated" : ""})
        </span>
      </span>

      {verdict.requiredIroas !== null && budget ? (
        <span className="text-basic-grey-inactive">
          Needs incremental ROAS ≥ {verdict.requiredIroas.toFixed(1)}x at your{" "}
          {formatMoney(budget, verdict.currency)} budget
        </span>
      ) : null}
      {verdict.minBudget !== null && iroas ? (
        <span className="text-basic-grey-inactive">
          Spend ≥ {formatMoney(verdict.minBudget, verdict.currency)} at {iroas}x
          expected incremental ROAS
        </span>
      ) : null}
      {!budget && !iroas ? (
        <div className="flex flex-col gap-1">
          <span className="text-basic-grey-inactive">
            Add a planned budget or expected incremental ROAS to see whether it
            can produce a detectable lift.
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              placeholder="Planned budget"
              aria-label="Planned test budget"
              value={budgetDraft}
              onChange={event => setBudgetDraft(event.target.value)}
              width={130}
            />
            <Input
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              placeholder="Expected iROAS"
              aria-label="Expected incremental ROAS"
              value={iroasDraft}
              onChange={event => setIroasDraft(event.target.value)}
              width={130}
            />
            <Button
              size="xs"
              variant={{ variant: "outlined" }}
              disabled={!canSave || isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}

      <span className="text-basic-grey-inactive">
        By test length:{" "}
        {summary.horizons.map((horizon, index) => (
          <Fragment key={horizon.days}>
            {index > 0 ? " · " : ""}
            <span
              className={
                horizon.days === verdict.horizon.days
                  ? "font-medium text-white"
                  : ""
              }
            >
              {horizon.days}d {formatPct(horizon.mde)}
              {horizon.method === "extrapolated" ? "*" : ""}
            </span>
          </Fragment>
        ))}
        {anyExtrapolated
          ? " (* extrapolated — history too short to measure)"
          : ""}
      </span>

      {verdict.underpowered ? (
        <span className="text-warning">
          This design looks underpowered: {verdict.reasons.join("; ")}. Create a
          new experiment with a longer window or a larger control.
        </span>
      ) : null}
    </div>
  )
}
